import * as React from "react";
import DataList from "../DataList";
import "./workspaceContainer.scss"
import {api} from "../../../../api/api";
import {WorkspaceView} from "../../../../model/workspace/WorkspaceView";
import {none, option, Option} from "ts-option";
import FiltersContainer from "../../filters/filtersContainer/FiltersContainer";
import PageSelectionComponent from "../paginator/PageSelectionComponent";
import Loading from "../../../ui/loading/Loading";
import Toast from "../../../helper/Toast";
import ViewsContainer from "../../view/viewContainer/ViewsContainer";
import {SaveFiltersConfig} from "../../filters/button/SaveFiltersButtonConfigView";
import RenameViewModal from "../../modal/RenameViewModal";
import RemoveFromFavouritesModal from "../../modal/RemoveFromFavouritesModal";
import DeleteViewModal from "../../modal/DeleteViewModal";
import CopyViewModal from "../../modal/CopyViewModal";
import LocalStorage from "../../../../store/local/LocalStorage";
import WorkspaceViewsData from "../../../../model/workspace/WorkspaceViewsData";
import {notifyBugsnagAndShowToast} from "../../../../utils/Utils";
import SortColumnModel from "../../../../model/workspace/SortColumnModel";
import WildcardFilter, {WildcardFilterColumnType} from "../../../../model/workspace/WildcardFilter";
import WildcardSearch from "../filter/WildcardSearch";
import {CellInfo} from "react-table";
import IconWrapper from "../../../../hiberUiKit/src/atoms/Icon/IconWrapper";
import {Icon} from "../../../../hiberUiKit/src/atoms/Icon/Icon";
import IconLarge from "../../../../hiberUiKit/src/atoms/Icon/IconLarge";
import Filter from "../../../../model/workspace/Filter";
import {arrayMove} from "../../../../utils/ArrayUtils";
import WorkspaceMobileActions from "./WorkspaceMobileActions";
import _ = require("underscore");

interface ComponentProps {
	/**
	 * Workspace name.
	 */
	workspaceName: WorkspaceName;
	/**
	 * Data list row click callback.
	 * @param {string} rowId
	 * @param {any} rowData
	 */
	onRowClick?: (rowId: string, rowData: any) => void;
	/**
	 * Get row id from row object.
	 * @param row
	 * @returns {string}
	 */
	rowToId: (row: any) => string;
	/**
	 * Button to be rendered next to search input.
	 * @returns {JSX.Element}
	 */
	renderButton?: () => JSX.Element;
	/**
	 * Indicates if workspace columns are sortable
	 */
	sortable?: boolean
	/**
	 * Default sort
	 */
	defaultSort?: Option<SortColumnModel>
	/**
	 * Last column content
	 * @returns {JSX.Element}
	 */
	lastColumn?: (row: CellInfo) => JSX.Element;
	/**
	 * Custom width of last column.
	 */
	lastColumnWidth?: number;
	/**
	 * Returns if passed row is ignored.
	 * @param rowData
	 * @returns {boolean}
	 */
	isRowIgnored?: (rowData: any) => boolean;
	/**
	 * Page number user visited before he went to transaction details
	 */
	pageNo?: number
	/**
	 * Wildcard filter column type
	 */
	wildcardFilterColumnType: WildcardFilterColumnType
	/**
	 * On selection change
	 */
	onSelectionChange?: (selected: any[]) => void
	/**
	 * The number of items to display per page.
	 */
	pageSize?: number;
}

interface TableData {
	totalItems: number;
	currentPageNo: number;
	items: any[];
}

interface IState {
	views: Option<WorkspaceView[]>;
	selectedView: Option<WorkspaceView>;
	favouritesViews: string[];
	data: TableData;
	isLoading: boolean;
	viewToRename: Option<WorkspaceView>;
	viewToRemoveFromFavourites: Option<WorkspaceView>;
	viewToDelete: Option<WorkspaceView>;
	viewToCopy: Option<WorkspaceView>;
	wildcardFilter: Option<WildcardFilter>;
	sortColumn: Option<SortColumnModel>
}

type Props = ComponentProps;

export default class WorkspaceContainer extends React.Component<Props, IState> {

	dataListRef: any;
	public static PAGE_SIZE = 20;
	viewIdFromLocalStorage: string = null;
	workspacesWithBreadcrumbs = [WorkspaceName.Assets, WorkspaceName.PartnerBrands];

	constructor(props: Props) {
		super(props);
		this.state =
			{
				views: none,
				selectedView: none,
				favouritesViews: [],
				data: {totalItems: 0, currentPageNo: props.pageNo || 1, items: []},
				isLoading: false,
				viewToRename: none,
				viewToRemoveFromFavourites: none,
				viewToDelete: none,
				viewToCopy: none,
				wildcardFilter: none,
				sortColumn: props.defaultSort ? props.defaultSort : none
			};

		this.onPageChange = this.onPageChange.bind(this);
		this.saveView = this.saveView.bind(this);
		this.renameView = this.renameView.bind(this);
		this.removeFromFavourites = this.removeFromFavourites.bind(this);
		this.addViewToFavourites = this.addViewToFavourites.bind(this);
		this.reorderFavouritesViews = this.reorderFavouritesViews.bind(this);
		this.deleteView = this.deleteView.bind(this);
		this.copyView = this.copyView.bind(this);
		this.copyToMyViews = this.copyToMyViews.bind(this);
		this.refreshCurrentPage = this.refreshCurrentPage.bind(this);
		this.onFiltersChange = this.onFiltersChange.bind(this);

		this.viewIdFromLocalStorage = LocalStorage.getWorkspaceViewId(this.props.workspaceName);
	}

	componentDidMount() {
		const workspaceFilters: Filter[] = LocalStorage.getWorkspaceFilters(this.props.workspaceName);
		const workspaceSearch: string = LocalStorage.getWorkspaceSearch(this.props.workspaceName);
		this.getViews().then((viewsData => {
					const view = viewsData.views.filter(v => v.id == this.viewIdFromLocalStorage)[0];
					this.setState({
						views: this.applyAlphabeticalOrder(viewsData.views),
						selectedView: option(view ? view : viewsData.views[0]),
						favouritesViews: viewsData.favourites,
					}, () => {
						if (workspaceFilters.length > 0) {
							this.onFiltersChange(workspaceFilters, false);
						}
						if (workspaceSearch) {
							this.searchWithSavedQuery(workspaceSearch);
						}
						else {
							this.refreshCurrentPage();
						}
					})
				}
			)
		).catch((err) => notifyBugsnagAndShowToast(err, "Fetch views"));
	}

	render() {
		const displayBreadcrumbs = this.workspacesWithBreadcrumbs.some(name => name === this.props.workspaceName);
		const tableData = this.state.data;
		const loadingText = !this.state.isLoading && this.state.data.items.length == 0 ?
			`No ${this.props.workspaceName.replaceUnderscoresWithSpaces()} found` : "";
		return (
			<Loading isLoading={this.state.selectedView.isEmpty && this.state.wildcardFilter.isEmpty}>
				{this.state.selectedView.map(view => (
						<div className={"c-workspace"}>
							{displayBreadcrumbs && this.renderBreadcrumbs()}
							{this.renderWorkspaceTitle()}
							<ViewsContainer views={this.state.views.getOrElse([])}
											selectedView={view}
											favouritesViews={this.state.favouritesViews}
											onViewSelect={(view) => this.setState({
													selectedView: option(view),
													wildcardFilter: none
												},
												() => {
													this.saveSelectedViewToLocalStorage();
													this.refreshData()
												})}
											onRenameView={(view) => this.setState({viewToRename: option(view)})}
											onRemoveFromFavourite={(view) => this.setState(
												{viewToRemoveFromFavourites: option(view)})}
											onFavouritesViewsOrderChange={this.reorderFavouritesViews}
											onDeleteView={(view) => this.setState({viewToDelete: option(view)})}
											onDuplicateView={(view) => this.setState({viewToCopy: option(view)})}
											onAddToFavourites={this.addViewToFavourites}
											onCopyToMyView={this.copyToMyViews}
											ignoreSelection={this.state.wildcardFilter.isDefined}/>
							<div className={"c-workspace_actions"}>
								<div className={"actions-wrapper"}>
									<WildcardSearch onFilterChange={(filter) => this.setState({wildcardFilter: filter},
										() => this.onPageChange(1))}
													isLoading={this.state.isLoading}
													filterValue={this.state.wildcardFilter}
													workspaceName={this.props.workspaceName}/>
									<FiltersContainer columns={view.columns}
													  filters={view.filters}
													  onSaveView={this.saveView}
													  onFiltersChange={this.onFiltersChange}
													  isExpanded={false}
													  workspaceName={this.props.workspaceName}/>

								</div>
								<PageSelectionComponent itemsNumber={tableData.totalItems}
														itemsPerPage={this.props.pageSize || WorkspaceContainer.PAGE_SIZE}
														currentPageIndex={tableData.currentPageNo}
														onChange={this.onPageChange}/>
							</div>
							<DataList ref={ref => this.dataListRef = ref}
									  columns={view.columns}
									  onColumnsChange={(columns) => this.setState({selectedView: option(view.copy({columns}))})}
									  data={this.state.data.items}
									  onRowClick={(rowId, rowData) => this.props.onRowClick ?
										  this.props.onRowClick(rowId, rowData) : null}
									  rowToId={this.props.rowToId}
									  isLoading={this.state.isLoading}
									  noDataText={loadingText}
									  onSortingChange={(sortColumn) => { this.setState({sortColumn}, this.refreshData) }}
									  sortColumn={this.state.sortColumn}
									  sortable={this.props.sortable}
									  lastColumn={this.props.lastColumn}
									  lastColumnWidth={this.props.lastColumnWidth}
									  refreshCurrentPage={this.refreshCurrentPage}
									  isRowIgnored={this.props.isRowIgnored}
									  workspaceName={this.props.workspaceName}
									  onSelectedCheckboxesChange={this.onSelectedCheckboxesChange}
									  pageSize={this.props.pageSize || WorkspaceContainer.PAGE_SIZE}
							/>
							{/* Under the cards as well, shown on a phone only: a hundred cards is a long scroll back up. */}
							<div className={"c-workspace_bottom-pages"}>
								<PageSelectionComponent itemsNumber={tableData.totalItems}
														itemsPerPage={this.props.pageSize || WorkspaceContainer.PAGE_SIZE}
														currentPageIndex={tableData.currentPageNo}
														onChange={this.onPageChange}/>
							</div>
						</div>
					)
				).orNull
				}
				{this.state.viewToRename.map(v =>
					<RenameViewModal onClose={() => this.setState({viewToRename: none})}
									 onSave={this.renameView}
									 viewName={v.name}/>
				).orNull}
				{this.state.viewToRemoveFromFavourites.map(v =>
					<RemoveFromFavouritesModal onClose={() => this.setState({viewToRemoveFromFavourites: none})}
											   onSave={this.removeFromFavourites}
											   viewName={v.name}/>
				).orNull}
				{this.state.viewToDelete.map(v =>
					<DeleteViewModal onClose={() => this.setState({viewToDelete: none})}
									 onSave={this.deleteView}
									 viewName={v.name}
									 isGlobal={v.global}/>
				).orNull}
				{this.state.viewToCopy.map(v =>
					<CopyViewModal onClose={() => this.setState({viewToCopy: none})}
								   onSave={this.copyView}
								   viewName={v.name}/>
				).orNull}
			</Loading>
		)
	}

	onSelectedCheckboxesChange = (indexes: string[]) => {
		if(this.props.onSelectionChange) {
			const data = indexes.map(index => this.state.data.items[parseInt(index)]);
			this.props.onSelectionChange(data.filter(d => d));
		}
	};

	searchWithSavedQuery = (searchQuery: string) => {
		const wildcardFilter = new WildcardFilter(searchQuery);
		this.setState({wildcardFilter: option(wildcardFilter)},
			() => this.onPageChange(1))
	};

	onFiltersChange(filters: Filter[], refreshData: boolean) {
		this.state.selectedView.map(view => {
			let newState: any = {
				selectedView: option(view.copy({filters}))
			};
			refreshData && (newState.wildcardFilter = none);
			this.setState(newState,
				() => {
					LocalStorage.saveWorkspaceFilters(this.props.workspaceName, filters);
					refreshData && this.refreshData();
				});
		})
	}

	onPageChange(pageNo: number, wildcardFilter: WildcardFilter = this.state.wildcardFilter.orNull) {
		this.setState({isLoading: true}, () =>
			this.getData(pageNo, wildcardFilter).then((data) => {
					if (pageNo > 1 && !data.items.length) {
						this.refreshData();
					}
					else {
						this.setState(
							{
								data: {
									totalItems: data.totalItems,
									currentPageNo: pageNo,
									items: data.items
								},
								isLoading: false,
							}, () => {
								if (wildcardFilter) {
									LocalStorage.saveWorkspaceSearch(this.props.workspaceName, wildcardFilter.value);
								}
								else {
									LocalStorage.saveWorkspaceSearch(this.props.workspaceName, "");
								}
							});
					}
					if(this.dataListRef && this.dataListRef.clearCheckBoxSelection) {
						this.dataListRef.clearCheckBoxSelection();
					}
				}
			).catch((err) => this.setState({isLoading: false}, () => notifyBugsnagAndShowToast(err, "Fetch workspace data")))
		)
	}

	getViews(): Promise<WorkspaceViewsData> {
		return api.instance.workspaceApi.fetchWorkspaceViews(this.props.workspaceName);
	}

	refreshData() {
		this.onPageChange(1);
	}

	refreshCurrentPage() {
		this.onPageChange(this.state.data.currentPageNo);
	}

	getData(pageNumber: number, wildcardFilter?: WildcardFilter) {
		let filters = [];
		if (wildcardFilter) {
			filters = [wildcardFilter.toQueryJson(this.props.wildcardFilterColumnType)];
		}
		else {
			filters = this.state.selectedView.map(sv => sv.filters.filter(f => f.type !== null).map(f => f.toQueryJson()))
				.getOrElse([]);
		}
		const sort = this.state.sortColumn.map(sc => sc.toQueryString()).orNull;
		return api.instance.workspaceApi.fetchWorkspaceData(this.props.workspaceName, pageNumber, filters, sort, this.props.pageSize);
	}

	saveView(config: SaveFiltersConfig) {
		this.state.selectedView.map(sv =>
			this.setState({isLoading: true}, () =>
				api.instance.workspaceApi.saveView(this.props.workspaceName, config.name, config.addToFavorite,
					!config.saveOnlyForMe, sv.columns, sv.filters)
					.then(view => {
						this.setState({
							views: this.applyAlphabeticalOrder(this.state.views.getOrElse([]).concat(view)),
							selectedView: option(view),
							favouritesViews: config.addToFavorite ? [...this.state.favouritesViews].concat(view.id) :
								this.state.favouritesViews,
							isLoading: false
						}, () => this.saveSelectedViewToLocalStorage())
					}).catch((err) => {
					this.setState({isLoading: false}, () => notifyBugsnagAndShowToast(err, "Save view"));
				})));
	}

	renameView(name: string) {
		this.state.viewToRename.map(vtr =>
			api.instance.workspaceApi.renameView(vtr.id, name, this.props.workspaceName).then(
				() =>
					this.setState({
						views: this.applyAlphabeticalOrder(
							this.state.views.getOrElse([]).map(v => v.id == vtr.id ? v.copy({name}) : v)),
						selectedView: this.state.selectedView.map(sv => sv.id == vtr.id ? sv.copy({name}) : sv),
						viewToRename: none
					}, () => Toast.success("View name changed successfully", "Rename view"))
			).catch((err) => this.setState({viewToRename: none}, () => notifyBugsnagAndShowToast(err, "Rename view"))))
	}

	removeFromFavourites() {
		this.state.viewToRemoveFromFavourites.map(vtrff =>
			api.instance.workspaceApi.removeFromFavourites(vtrff.id, this.props.workspaceName).then(
				() =>
					this.setState({
						viewToRemoveFromFavourites: none,
						favouritesViews: this.state.favouritesViews.filter(vId => vId != vtrff.id)
					}, () => Toast.success("View removed from favourites successfully", "Remove from favourite"))
			).catch((err) => this.setState({viewToRemoveFromFavourites: none},
				() => notifyBugsnagAndShowToast(err, "Remove from favourite"))))
	}

	reorderFavouritesViews(dragged: number, target: number) {
		const fv = arrayMove(this.state.favouritesViews, dragged, target);
		api.instance.workspaceApi.reorderFavouritesViews(this.props.workspaceName, fv)
			.then(() =>
				this.setState({favouritesViews: fv},
					() => Toast.success("Order favourites views changed successfully", "Change order")))
			.catch((err) => notifyBugsnagAndShowToast(err, "Change order"));
	}

	deleteView() {
		this.state.viewToDelete.map(vtd =>
			api.instance.workspaceApi.deleteView(vtd.id, this.props.workspaceName).then(
				() =>
					this.setState({
						viewToDelete: none,
						favouritesViews: this.state.favouritesViews.filter(vId => vId != vtd.id),
						views: option(this.state.views.getOrElse([]).filter(v => v.id != vtd.id)),
						selectedView: this.state.selectedView.map(sv => sv.id).getOrElse("") == vtd.id ?
							option(this.state.views.getOrElse([])[0]) : this.state.selectedView
					}, () => {
						Toast.success("View deleted successfully", "Delete view");
						this.saveSelectedViewToLocalStorage();
					})
			).catch((err) => this.setState({viewToDelete: none}, () => notifyBugsnagAndShowToast(err, "Delete view"))))
	}

	copyView(viewName: string) {
		this.state.viewToCopy.map(vts =>
			api.instance.workspaceApi.saveView(this.props.workspaceName, viewName, false, vts.global, vts.columns, vts.filters)
				.then(view => {
					this.setState({
						viewToCopy: none,
						views: this.applyAlphabeticalOrder(this.state.views.getOrElse([]).concat(view)),
						selectedView: option(view),
					}, () => {
						Toast.success("View copied successfully", "Copy view");
						this.saveSelectedViewToLocalStorage();
					})
				}).catch((err) => {
				this.setState({viewToCopy: none}, () => notifyBugsnagAndShowToast(err, "Copy view"));
			})
		);
	}

	addViewToFavourites(viewId: string) {
		api.instance.workspaceApi.addViewToFavourites(viewId, this.props.workspaceName)
			.then(() => this.setState({favouritesViews: this.state.favouritesViews.concat(viewId)},
				() => Toast.success("View added to favourites successfully", "Add view to favourites")))
			.catch((err) => notifyBugsnagAndShowToast(err, "Add view to favourites"))
	}

	saveSelectedViewToLocalStorage() {
		this.state.selectedView.map(sv => {
			LocalStorage.saveSelectedWorkspaceView(this.props.workspaceName, sv.id);
			LocalStorage.saveWorkspaceFilters(this.props.workspaceName, sv.filters);
		});
	}

	copyToMyViews(view: WorkspaceView) {
		api.instance.workspaceApi.saveView(this.props.workspaceName, view.name, false, false, view.columns, view.filters)
			.then(view => {
				this.setState({
					views: this.applyAlphabeticalOrder(this.state.views.getOrElse([]).concat(view)),
					selectedView: option(view)
				}, () => {
					this.saveSelectedViewToLocalStorage();
					Toast.success("View copied to my views successfully", "Copy to my views");
				})
			}).catch((err) => notifyBugsnagAndShowToast(err, "Copy to my views"));
	}

	renderBreadcrumbs = () =>
		<div className={'breadcrumbs'}>
			<ul className={'breadcrumbs-list'}>
				<li className={'breadcrumbs-section'}>
					{this.props.workspaceName.replaceUnderscoresWithSpaces()}
					<span className={'breadcrumbs-separator'}>
						<IconWrapper variant={"lighterBlue"} icon={Icon.ArrowRight} size={11}/>
					</span>
				</li>
				<li>{this.props.workspaceName.capitalize().replaceUnderscoresWithSpaces()} workspace</li>
			</ul>
		</div>;

	renderWorkspaceTitle = () =>
		<div className={'c-workspace-title'}>
			<h1>
				<IconLarge icon={Icon.Job}/>
				<span>{this.props.workspaceName.capitalize().replaceUnderscoresWithSpaces()} workspace</span>
			</h1>
			<div className={'c-workspace-title_buttons'}>{this.props.renderButton && this.props.renderButton()}</div>
			{this.props.renderButton && <WorkspaceMobileActions renderButton={this.props.renderButton}/>}
		</div>;

	applyAlphabeticalOrder(views: WorkspaceView[]) {
		const defaultAllViewsOption = views[0];
		const sortedViews = _.tail(views).sort(this.sortByName);
		return option([defaultAllViewsOption].concat(sortedViews))
	}

	sortByName = (wv1: WorkspaceView, wv2: WorkspaceView) => {
		return wv1.name.localeCompare(wv2.name)
	}
}

export enum WorkspaceName {
	Leads = "leads",
	Transactions = "transactions",
	Jobs = "jobs",
	Materials = "materials",
	Assets = "assets",
	Bills = "bills",
	PartnerBrands = "partner_brands"
}